// src/features/treinos/components/SessaoFinalizada.jsx
//
// Tela "Sessão finalizada" (Partes I/J/K). Porta a coreografia do protótipo
// protipo-animacoes/lesvita-celebracao-treino-concluido.html para React:
//   armIn → flex (2x) → pop do músculo → copy "Treino concluído!" → sobe +
//   pulso em LOOP, com o bloco de infos entrando embaixo.
// Hooks de SFX: onArmAppear (#1) e onMusclePop (#2) — áudios entram depois.
// [SWAP ASSET]: troca o emoji 💪 pelo bíceps 3D no marcador abaixo.
//
// CONCLUÍDO → celebração + "Fazer check-in". ABANDONADO → resumo NEUTRO,
// sem animação e sem check-in (Parte I/decisão 2).

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaClipboardList,
  FaDumbbell,
  FaRegClock,
  FaFireAlt,
  FaStar,
  FaTrophy,
} from "react-icons/fa";
import { useUser } from "../../../contexts/UserContext.jsx";
import { useLiveWorkout, useLiveWorkoutTimer } from "../../../contexts/LiveWorkoutContext.jsx";
import { fetchWorkoutSessions } from "../services/exerciseStatsService.js";
import { exMatches, normNome } from "../utils/exerciseStats.js";
import Style from "../../../styles/features/treinos/SessaoFinalizada.module.css";

// Timings (espelho do protótipo TIMINGS; armAppearDelay reduzido p/ UX no app).
const T = { armIn: 350, flex: 420, pop: 880, copy: 1500, rise: 2300, done: 620 };

const prefersReduced = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Guard de sessão: a coreografia de celebração roda UMA vez por sessaoId. Se o
// componente remontar (ou o efeito re-disparar), exibimos o estado final sem
// repetir a entrada — corrige o "aparece, apaga e aparece de novo".
const _celebrated = new Set();

function fmtMin(secs) {
  const m = Math.round((secs || 0) / 60);
  return `${m} min`;
}

export default function SessaoFinalizada({ onArmAppear, onMusclePop }) {
  const { user } = useUser();
  const {
    exState,
    totais,
    metrics,
    summaryStatus,
    session,
    startLiveCheckin,
    closeSession,
  } = useLiveWorkout();
  const { elapsed } = useLiveWorkoutTimer();

  const celebrate = summaryStatus === "CONCLUIDO";

  // ── PRs batidos (≥2ª aparição do exercício) ────────────────────────────────
  const [prSet, setPrSet] = useState(() => new Set());
  useEffect(() => {
    let alive = true;
    const uid = user?.uid;
    if (!uid) return undefined;
    fetchWorkoutSessions(uid).then((sessions) => {
      if (!alive) return;
      const curId = session?.sessaoId;
      const prs = new Set();
      exState.forEach((ex) => {
        const currentMax = Math.max(
          0,
          ...ex.rows.filter((r) => r.done).map((r) => Number(r.carga) || 0),
        );
        if (currentMax <= 0) return;
        const nomeNorm = normNome(ex.NOME);
        let priorMax = 0;
        let priorAppearances = 0;
        sessions.forEach((s) => {
          if (s.id === curId || s.SESSAO_ID === curId) return; // exclui a atual
          const match = (s.EXERCICIOS || []).find((e) =>
            exMatches(e, { exerciseId: ex.EXERCISE_ID, nomeNorm }),
          );
          if (!match) return;
          const did = (match.SERIES || []).some((se) => Number(se.CARGA) > 0);
          if (!did) return;
          priorAppearances += 1;
          (match.SERIES || []).forEach((se) => {
            const c = Number(se.CARGA) || 0;
            if (c > priorMax) priorMax = c;
          });
        });
        // PR só a partir da 2ª aparição (já houve ≥1 sessão anterior com carga).
        if (priorAppearances >= 1 && currentMax > priorMax) prs.add(ex._id);
      });
      setPrSet(prs);
    });
    return () => { alive = false; };
  }, [user?.uid, exState, session?.sessaoId]);

  // ── Coreografia ────────────────────────────────────────────────────────────
  const reduced = useMemo(() => prefersReduced(), []);
  const armRef = useRef(null);
  const timersRef = useRef([]);
  const [st, setSt] = useState({
    armIn: false, flexing: false, pop: false, pulsing: false,
    copy: false, risen: false, infos: false,
  });

  // Partículas (porta burst() do protótipo via DOM no container do braço).
  const burst = (count = 22, lingerEvery = 4) => {
    const host = armRef.current;
    if (!host) return;
    for (let i = 0; i < count; i++) {
      const s = document.createElement("span");
      const linger = i % lingerEvery === 0;
      const ang = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const dist = linger ? 70 + Math.random() * 40 : 90 + Math.random() * 70;
      s.className = `${Style.spark}${linger ? " " + Style.sparkFloat : " " + Style.sparkGo}`;
      s.style.setProperty("--s", (linger ? 7 : 5 + Math.random() * 6) + "px");
      s.style.setProperty("--dx", Math.cos(ang) * dist + "px");
      s.style.setProperty("--dy", (Math.sin(ang) * dist - (linger ? 30 : 10)) + "px");
      s.style.setProperty("--rot", (Math.random() * 240 - 120) + "deg");
      s.style.setProperty("--dur", (linger ? 2400 + Math.random() * 600 : 900 + Math.random() * 500) + "ms");
      host.appendChild(s);
      window.setTimeout(() => s.remove(), 3200);
    }
  };

  useEffect(() => {
    if (!celebrate) return undefined;
    const sid = session?.sessaoId || "__live__";
    // Reduzido OU já celebrado nesta sessão → estado final, sem replay.
    if (reduced || _celebrated.has(sid)) {
      setSt({ armIn: true, flexing: false, pop: false, pulsing: !reduced, copy: true, risen: true, infos: true });
      return undefined;
    }
    _celebrated.add(sid);
    const after = (ms, fn) => timersRef.current.push(window.setTimeout(fn, ms));

    after(T.armIn, () => { setSt((s) => ({ ...s, armIn: true })); burst(24); onArmAppear?.(); });
    after(T.armIn + T.flex, () => setSt((s) => ({ ...s, flexing: true })));
    after(T.armIn + T.pop, () => {
      setSt((s) => ({ ...s, pop: true }));
      burst(14, 3);
      onMusclePop?.();
      timersRef.current.push(window.setTimeout(() => setSt((s) => ({ ...s, pop: false })), 380));
    });
    after(T.armIn + T.copy, () => setSt((s) => ({ ...s, copy: true })));
    after(T.armIn + T.rise, () =>
      setSt((s) => ({ ...s, flexing: false, risen: true, pulsing: true, copy: true, infos: true })),
    );

    return () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
  }, [celebrate, reduced, session?.sessaoId]);

  // ── Stats do resumo ────────────────────────────────────────────────────────
  const totalSeries = totais?.totalSeries ?? metrics.completedSets;
  const tonelagem = totais?.tonelagemTotal ?? metrics.volumeTotal;
  const prCount = prSet.size;

  const stats = [
    { ic: <FaClipboardList size={16} />, lbl: "Séries", val: String(totalSeries) },
    { ic: <FaDumbbell size={16} />, lbl: "Tonelagem total", val: `${Number(tonelagem).toLocaleString("pt-BR")} kg` },
    { ic: <FaRegClock size={16} />, lbl: "Duração", val: fmtMin(elapsed) },
    { ic: <FaFireAlt size={16} />, lbl: "Calorias", val: `${metrics.caloriasLive} kcal` },
    { ic: <FaStar size={16} />, lbl: "XP ganho", val: `+${metrics.xpLive} XP`, gold: true },
    { ic: <FaTrophy size={16} />, lbl: "PRs batidos", val: String(prCount) },
  ];

  const armCls = [
    Style.arm,
    st.armIn && Style.armIn,
    st.flexing && Style.armFlexing,
    st.pop && Style.armPop,
    st.pulsing && Style.armPulsing,
  ].filter(Boolean).join(" ");

  return (
    <div className={Style.screen}>
      {celebrate ? (
        <div className={`${Style.stage} ${st.risen ? Style.stageRisen : ""}`}>
          <div className={`${Style.hero} ${st.risen ? Style.heroRisen : ""}`}>
            <div className={armCls} ref={armRef}>
              {/* [SWAP ASSET] troque o emoji pelo bíceps 3D (mantenha .armGlyph) */}
              <span className={Style.armGlyph}>💪</span>
            </div>
            <div className={`${Style.copy} ${st.copy ? Style.copyShown : ""}`}>
              <h1>Treino concluído!</h1>
              <p>Bom trabalho — seus dados foram registrados pelo treino ao vivo.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={Style.neutralHead}>
          <h1>Sessão encerrada</h1>
          <p>Suas séries registradas foram salvas. Nenhum check-in foi criado.</p>
        </div>
      )}

      <div className={`${Style.infoSlot} ${(!celebrate || st.infos) ? Style.infoSlotShown : ""}`}>
        <div className={Style.infoCard}>
          {stats.map((s) => (
            <div key={s.lbl} className={Style.infoRow}>
              <div className={Style.infoIc}>{s.ic}</div>
              <div className={Style.infoTxt}>
                <span className={Style.lbl}>{s.lbl}</span>
                <span className={`${Style.val} ${s.gold ? Style.valGold : ""}`}>{s.val}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Lista de exercícios: nome + nº de séries · tag PR */}
        <div className={Style.exList}>
          {exState.map((ex) => {
            const n = ex.rows.filter((r) => r.done).length;
            return (
              <div key={ex._id} className={Style.exItem}>
                <span className={Style.exNome}>
                  {ex.NOME}
                  {prSet.has(ex._id) && <span className={Style.prTag}>PR</span>}
                </span>
                <span className={Style.exCount}>{n} {n === 1 ? "série" : "séries"}</span>
              </div>
            );
          })}
        </div>

        {celebrate ? (
          <button type="button" className={Style.cta} onClick={startLiveCheckin}>
            Fazer check-in
          </button>
        ) : (
          <button type="button" className={Style.ctaGhost} onClick={closeSession}>
            Fechar
          </button>
        )}
      </div>
    </div>
  );
}
